import { useEffect, useState } from 'react';
import { paymentsGateway } from '../../payments';
import type { PaymentBatchResponse, PaymentTransactionResponse, PayrollRunSummary } from '../../payments/types';
import { formatReportCurrency, formatReportDate } from '../../reports/formatting';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { ActiveFilterChips } from '../components/ActiveFilterChips';
import { downloadCsv } from '../csvExport';
import '../accounting-reports.css';

function runLabel(run: PayrollRunSummary): string {
  return `${formatReportDate(run.periodStartDate)} – ${formatReportDate(run.periodEndDate)} · Check date ${formatReportDate(run.checkDate)}`;
}

function statusBadgeClass(status: string): string {
  if (status === 'Settled') return 'badge badge--success';
  if (status === 'Failed') return 'badge badge--blocked';
  return 'badge badge--warning';
}

/** EPN-77 Funding & Bank Transactions report. Reads real `PaymentBatchResponse`/`PaymentTransactionResponse`
 * history via the existing `paymentsGateway` (EPN.PaymentProcessing.Api) — no new backend. EPN.Employees.Api
 * has no bank-account field at all, and no frontend gateway today exposes the company's own masked funding
 * account (`CompanyFundingAccount.MaskedAccountLast4` exists server-side but only behind the onboarding
 * flow, not a general read endpoint) — so the Funding Account column intentionally shows the funding
 * partner/processor only, never a fabricated masked account number. Every transaction here is a net-pay
 * disbursement; PaymentTransaction has no transaction-type or debit/credit field to classify further. */
export function FundingTransactionsReportPage() {
  const [runs, setRuns] = useState<PayrollRunSummary[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const [batches, setBatches] = useState<PaymentBatchResponse[]>([]);
  const [transactionsByBatch, setTransactionsByBatch] = useState<Record<string, PaymentTransactionResponse[]>>({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    paymentsGateway
      .listApprovedPayrollRuns(controller.signal)
      .then((result) => {
        const sorted = [...result].sort((a, b) => b.checkDate.localeCompare(a.checkDate));
        setRuns(sorted);
        setSelectedRunId(sorted[0]?.id ?? null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setRunsError(friendlyErrorMessage(err, 'Could not load approved payroll runs.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setRunsLoading(false);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!selectedRunId) return;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDetailLoading(true);
    setDetailError(null);
    setBatches([]);
    setTransactionsByBatch({});
    paymentsGateway
      .listBatchesForRun(selectedRunId, controller.signal)
      .then(async (batchRows) => {
        if (controller.signal.aborted) return;
        setBatches(batchRows);
        const entries = await Promise.all(
          batchRows.map(async (batch) => [batch.id, await paymentsGateway.listTransactions(batch.id, controller.signal)] as const),
        );
        if (!controller.signal.aborted) setTransactionsByBatch(Object.fromEntries(entries));
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setDetailError(friendlyErrorMessage(err, 'Could not load funding transactions for this run.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });
    return () => controller.abort();
  }, [selectedRunId]);

  const selectedRun = runs.find((r) => r.id === selectedRunId) ?? null;
  const allTransactions = batches.flatMap((batch) => (transactionsByBatch[batch.id] ?? []).map((txn) => ({ batch, txn })));
  const totalAmount = allTransactions.reduce((sum, { txn }) => sum + txn.amount, 0);

  const chips = selectedRun
    ? [{ key: 'run', label: `Payroll run: ${formatReportDate(selectedRun.checkDate)}`, onRemove: () => setSelectedRunId(runs[0]?.id ?? null) }]
    : [];

  function exportCsv() {
    if (allTransactions.length === 0) return;
    downloadCsv(
      `funding-transactions-${selectedRunId}.csv`,
      ['Transaction Date', 'Reference', 'Description', 'Amount', 'Status'],
      allTransactions.map(({ batch, txn }) => [
        batch.settledAtUtc ?? batch.initiatedAtUtc ?? '', batch.providerFileId ?? '', 'Net Pay Disbursement', txn.amount.toFixed(2), batch.status,
      ]),
    );
  }

  return (
    <section className="card card--wide" aria-labelledby="funding-transactions-heading">
      <div className="section-header">
        <h2 id="funding-transactions-heading">Funding &amp; Bank Transactions</h2>
        <button type="button" onClick={exportCsv} disabled={allTransactions.length === 0}>Export CSV</button>
      </div>
      <p className="muted">Only transaction states Escochex can reliably confirm are shown — full account and routing numbers never appear here.</p>

      {runsLoading && <p className="muted" role="status">Loading approved payroll runs…</p>}
      {runsError && <p className="error" role="alert">{runsError}</p>}
      {!runsLoading && !runsError && runs.length === 0 && (
        <p className="report-empty-state">No approved payroll runs yet.</p>
      )}

      {!runsLoading && runs.length > 0 && (
        <div className="report-filter-bar">
          <div className="report-filter-field">
            <label htmlFor="funding-run">Payroll run</label>
            <select id="funding-run" value={selectedRunId ?? ''} onChange={(e) => setSelectedRunId(e.target.value)}>
              {runs.map((r) => (
                <option key={r.id} value={r.id}>{runLabel(r)}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <ActiveFilterChips chips={chips} onClearAll={() => setSelectedRunId(runs[0]?.id ?? null)} />

      {detailLoading && <p className="muted" role="status">Loading funding transactions…</p>}
      {detailError && <p className="error" role="alert">{detailError}</p>}

      {!detailLoading && !detailError && selectedRunId && batches.length === 0 && (
        <p className="report-empty-state">No funding batch has been initiated for this payroll run yet.</p>
      )}

      {allTransactions.length > 0 && (
        <>
          <table aria-label="Funding and bank transactions">
            <thead>
              <tr>
                <th scope="col">Transaction Date</th>
                <th scope="col">Reference</th>
                <th scope="col">Payroll Run</th>
                <th scope="col">Description</th>
                <th scope="col">Funding Account</th>
                <th scope="col">Amount</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {allTransactions.map(({ batch, txn }) => (
                <tr key={txn.id}>
                  <td>{batch.settledAtUtc ? formatReportDate(batch.settledAtUtc.slice(0, 10)) : batch.initiatedAtUtc ? formatReportDate(batch.initiatedAtUtc.slice(0, 10)) : '—'}</td>
                  <td>{batch.providerFileId ?? '—'}</td>
                  <td>{selectedRun ? formatReportDate(selectedRun.checkDate) : '—'}</td>
                  <td>Net Pay Disbursement</td>
                  <td><span className="report-not-tracked">Company payroll account</span></td>
                  <td>{formatReportCurrency(txn.amount)}</td>
                  <td><span className={statusBadgeClass(batch.status)}>{batch.status}</span></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={5}>Totals</th>
                <td>{formatReportCurrency(totalAmount)}</td>
                <td />
              </tr>
            </tfoot>
          </table>

          <p className="muted">
            The specific funding account (e.g. "Payroll Account ••••1234") is not yet exposed by any read endpoint outside
            company onboarding — shown as a generic label here rather than a fabricated account number.
          </p>
        </>
      )}
    </section>
  );
}
